import React, { useContext, useRef, useState } from "react";

import { Preview } from "@creatomate/preview";
import { Dropdown, Icon, Loader } from "@culturehq/components";

import { ElementSource, MediaVersion } from "chq";
import { useProfile } from "app/context";
import { dates, errorService, Trans } from "app/utils";
import { downloadMediaFile } from "app/utils/downloadMediaFile";

import MediaVersionLazyLoading from "components/admin/settings/LastIntersectedElement";
import ErrorMessage from "components/common/ErrorMessage";
import { CreatomateContext } from "../../contexts/VideoStudioContext";
import { isStoryElement, syncAspectRatio } from "../../utils";
import MediaVersionHistoryElement from "./MediaVersionHistoryElement";
import { activateDefaultLanguage, needToBlockSubtitles } from "../../sidebar/tabs/subtitle/utils";

interface MediaVersionHistoryProps {
  preview: Preview;
}

const Autosave = ({ autosave: { isAutosaving, done, error } }: any) => {
  let message = "";
  if (isAutosaving) message = "Saving...";
  else if (done) message = "Draft";
  else if (error) message = error;

  if (!message) return null;
  return <span className="autosave">({message})</span>;
};

const MediaVersionHistory: React.FC<MediaVersionHistoryProps> = ({ preview }) => {
  const {
    data,
    handleUpdateData,
    handleAutosaveDraft,
    restartData
  } = useContext(CreatomateContext);
  const {
    mediaVersionHistory,
    montage,
    onThirdPartyMediasReload,
    selectedMediaVersion,
    lastMediaVersion,
    story,
    thirdPartyMediaIsFetching,
    autosave
  } = data;

  const { profile: { organization } } = useProfile();
  const feedRef = useRef<HTMLDivElement>();
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (!story || !mediaVersionHistory || !selectedMediaVersion?.thirdPartyRenderId) return null;

  const handleMediaVersionChange = async (value: string) => {
    const version = JSON.parse(value);

    // const subtitles = getFilteredSubtitles(version);
    const versionBurnSubtitles = version.burnSubtitles ?? false;

    // if there are no subtitles in the story, we need to make sure that we remove the
    // burned subtitles in case we have burned subtitles in the json
    const creatomateJson = version.additionalData;
    creatomateJson.multipart = undefined;

    await preview.setSource(creatomateJson, true);
    const source = preview.getSource();
    const video = source.elements.find((element: ElementSource) => element.type === "video" && isStoryElement(element));
    await preview.setActiveElements([video?.id]);

    syncAspectRatio(handleUpdateData, preview);
    await handleAutosaveDraft({
      burnSubtitles: versionBurnSubtitles,
      selectedMediaVersion: version
    });
    await activateDefaultLanguage(preview, story);

    handleUpdateData({
      blockSubtitles: needToBlockSubtitles(
        creatomateJson || {}, lastMediaVersion as MediaVersion
      ),
      activeMenuItem: montage ? "stories" : "media",
      selectedTemplate: undefined
    });
  };

  const handleDownload = async (thirdPartyRenderId: string) => {
    const version = thirdPartyRenderId
      ? mediaVersionHistory.find(v => v.thirdPartyRenderId === thirdPartyRenderId)
      : mediaVersionHistory.find(v => v.original);

    if (!version) return;

    // Try to get the download URL from multiple sources
    let downloadDataUrl = version.url;
    if ((!downloadDataUrl || downloadDataUrl === "") && version.additionalData?.elements?.length > 0) {
      const { source, name } = version.additionalData.elements[0] || {};
      downloadDataUrl = source || name || "";
    }

    // If there is no valid URL, show an error and download the current version
    if (!downloadDataUrl) {
      const msg = "No valid URL for this version. Downloading the current version instead";
      setDownloadError(msg);
      errorService.error(msg);
      // Use the current version from the story
      const currentUrl = story?.media?.url || story?.media?.originalUrl;
      if (!currentUrl) return;
      const mediaType = story?.media?.mediaType || "video";
      try {
        await downloadMediaFile({
          url: currentUrl,
          mediaType: mediaType as "image" | "video",
          organization: organization?.name ? { name: organization.name } : undefined
        });
      } catch (error) {
        errorService.error(error);
      }
      return;
    }

    // Detect media type dynamically
    const mediaType = story?.media?.mediaType || "video";

    try {
      await downloadMediaFile({
        url: downloadDataUrl,
        mediaType: mediaType as "image" | "video",
        organization: organization?.name ? { name: organization.name } : undefined
      });
    } catch (error) {
      errorService.error(error);
    }
  };

  return (
    <div className="MediaVersion">
      {downloadError && <ErrorMessage error={downloadError} />}
      <Dropdown selected="" onChange={handleMediaVersionChange}>
        <Dropdown.Button className="btn-dropdown">
          <Icon icon="cloud" />
          <span>{dates.format(selectedMediaVersion.createdAt, "MM/DD/YY hh:mm a")}</span>
          <Autosave autosave={autosave} />
        </Dropdown.Button>

        <Dropdown.ListBox className="options">
          <div className="title__container">
            <span className="title"><Trans>Version History</Trans></span>
            <span className="subtitle"><Trans>Restore older versions of this video</Trans></span>
          </div>
          {mediaVersionHistory?.map(({ createdAt, creator, thirdPartyRenderId, ...rest }) => (
            <Dropdown.Option
              className="option"
              key={thirdPartyRenderId}
              value={JSON.stringify({ ...rest, createdAt, creator, thirdPartyRenderId })}
            >
              <MediaVersionHistoryElement
                createdAt={createdAt}
                creatorName={creator?.name || ""}
                original={rest.original || false}
                draft={rest.draft || false}
                checked={thirdPartyRenderId === selectedMediaVersion.thirdPartyRenderId}
                onDownload={() => handleDownload(thirdPartyRenderId)}
              />
            </Dropdown.Option>
          ))}
          {thirdPartyMediaIsFetching && <Loader loading />}
          <MediaVersionLazyLoading
            rootRef={feedRef}
            onNearScreen={onThirdPartyMediasReload}
          />
        </Dropdown.ListBox>
      </Dropdown>
    </div>
  );
};

export default MediaVersionHistory;
